import React, { createContext, useContext, useState, useEffect } from 'react';
import proveedoresService from '../services/proveedoresService';
import { useCompany } from './CompanyContext';

/**
 * ------------------------------------------------------------------
 * PROVEEDOR CONTEXT
 * ------------------------------------------------------------------
 * Maneja el estado global de proveedores
 * Proporciona funciones CRUD para proveedores
 * ------------------------------------------------------------------
 */

const ProveedorContext = createContext();

export function useProveedores() {
    const context = useContext(ProveedorContext);
    if (!context) {
        throw new Error('useProveedores debe usarse dentro de un ProveedorProvider');
    }
    return context;
}

export function ProveedorProvider({ children }) {
    const [proveedores, setProveedores] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { company } = useCompany();

    /**
     * Cargar todos los proveedores
     */
    /**
     * Cargar todos los proveedores
     * @param {boolean} force - Forzar recarga desde API
     */
    const fetchProveedores = async (force = false) => {
        if (!company || !company.rif) {
            console.warn('No hay empresa seleccionada para cargar proveedores');
            return;
        }

        // Si ya tenemos datos y no forzamos recarga, retornamos lo que hay (Cache)
        if (!force && proveedores.length > 0) {
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const data = await proveedoresService.getAll(company.rif);
            const normalizedData = Array.isArray(data) ? data : (data.data || []);
            setProveedores(normalizedData);
        } catch (err) {
            console.error('Error fetching proveedores:', err);
            setError(err.message || 'Error al cargar proveedores');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Obtener un proveedor por RIF
     */
    const getProveedorByRif = async (rif) => {
        setLoading(true);
        setError(null);
        try {
            const proveedor = await proveedoresService.getByRif(rif, company.rif);
            return proveedor;
        } catch (err) {
            console.error(`Error fetching proveedor ${rif}:`, err);
            setError(err.message || 'Error al cargar proveedor');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Crear un nuevo proveedor
     */
    const createProveedor = async (proveedorData) => {
        if (!company || !company.rif) {
            throw new Error('No hay empresa seleccionada');
        }

        setLoading(true);
        setError(null);
        try {
            // Agregar empresa_rif automáticamente (override si viene en data)
            const dataWithEmpresa = {
                ...proveedorData,
                empresa_rif: company.rif
            };

            const nuevoProveedor = await proveedoresService.create(dataWithEmpresa);

            // Agregar el nuevo proveedor a la lista
            setProveedores(prev => [...prev, nuevoProveedor]);

            return nuevoProveedor;
        } catch (err) {
            console.error('Error creating proveedor:', err);
            setError(err.message || 'Error al crear proveedor');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Actualizar un proveedor existente
     */
    const updateProveedor = async (rif, proveedorData) => {
        if (!company || !company.rif) {
            throw new Error('No hay empresa seleccionada');
        }

        setLoading(true);
        setError(null);
        try {
            // Agregar empresa_rif automáticamente
            const dataWithEmpresa = {
                ...proveedorData,
                empresa_rif: company.rif
            };

            const proveedorActualizado = await proveedoresService.update(rif, dataWithEmpresa);

            // Actualizar el proveedor en la lista
            setProveedores(prev =>
                prev.map(p => p.rif === rif ? proveedorActualizado : p)
            );

            return proveedorActualizado;
        } catch (err) {
            console.error(`Error updating proveedor ${rif}:`, err);
            setError(err.message || 'Error al actualizar proveedor');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Eliminar un proveedor
     */
    const deleteProveedor = async (rif) => {
        setLoading(true);
        setError(null);
        try {
            await proveedoresService.delete(rif, company.rif);

            // Remover el proveedor de la lista
            setProveedores(prev => prev.filter(p => p.rif !== rif));
        } catch (err) {
            console.error(`Error deleting proveedor ${rif}:`, err);
            setError(err.message || 'Error al eliminar proveedor');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Cargar proveedores cuando la empresa cambie
    // Limpiar proveedores cuando la empresa cambie (pero no cargar automáticamente)
    // Esto evita que se muestren proveedores de la empresa anterior
    useEffect(() => {
        setProveedores([]);
    }, [company?.rif]);

    const value = {
        proveedores,
        loading,
        error,
        fetchProveedores,
        getProveedorByRif,
        createProveedor,
        updateProveedor,
        deleteProveedor
    };

    return (
        <ProveedorContext.Provider value={value}>
            {children}
        </ProveedorContext.Provider>
    );
}
